import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeftIcon as ArrowLeft,
  SparkleIcon as Sparkle,
  WarningOctagonIcon as WarningOctagon,
  ArchiveIcon as Archive,
  ShieldWarningIcon as ShieldWarning,
  PackageIcon as Package,
  CircleNotchIcon as Spinner,
  RobotIcon as Robot,
  CopySimpleIcon as CopySimple,
  CheckCircleIcon as CheckCircle,
  GraphIcon as Graph,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getModernizationOpportunities,
  narrateModernizationOpportunities,
  getWorkspace,
} from "@/lib/api";
import Markdown from "@/components/Markdown";
import { knowledgeSummary } from "@/lib/knowledgeContext";

export default function WorkspaceMetadataOpportunities() {
  const { id } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [narrative, setNarrative] = useState("");
  const [narrating, setNarrating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cacheAge, setCacheAge] = useState(null); // seconds — null = fresh / no info
  const [knowledge, setKnowledge] = useState(null); // Iter 56 — knowledge_context summary of the memo prompt

  useEffect(() => {
    getWorkspace(id).then((d) => setWorkspace(d?.workspace || null));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    getModernizationOpportunities(id)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load opportunities."))
      .finally(() => setLoading(false));
  }, [id]);

  const runNarrate = async (force) => {
    setNarrating(true);
    const toastId = toast.loading(
      force ? "Regenerating AI recommendations…" : "Generating AI recommendations… (~10–20s)"
    );
    try {
      const res = await narrateModernizationOpportunities(id, { force });
      setNarrative(res.narrative);
      setCacheAge(res.cached ? res.age_seconds : null);
      setKnowledge(knowledgeSummary(res.knowledge));
      toast.success(
        res.cached
          ? `Showing cached recommendations from ${res.age_seconds}s ago.`
          : "Recommendations ready.",
        { id: toastId }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI narration failed.", { id: toastId });
    } finally {
      setNarrating(false);
    }
  };
  const handleNarrate = () => runNarrate(false);
  const handleRegenerate = () => runNarrate(true);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(narrative);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      toast.success("Memo copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const totals = {
    high_risk: data?.high_risk_files?.length || 0,
    orphans: data?.orphan_files?.length || 0,
    unjournaled: data?.unjournaled_transactional?.length || 0,
    micro: data?.microservice_candidates?.length || 0,
  };
  const findingsCount = totals.high_risk + totals.orphans + totals.unjournaled + totals.micro;

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/workspaces/${id}/metadata/dashboard`}
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={11} weight="bold" />
            Back to dashboard
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Sparkle size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Modernization opportunities
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
            AI-readiness analysis of this workspace&apos;s catalog — blast-radius
            ranked risks, archive candidates, journaling gaps, and clean
            microservice extraction targets.
          </p>
        </div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
          Workspace<br />
          <span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
        </div>
      </div>

      {loading ? (
        <div className="ml-card p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
          <Spinner size={18} className="animate-spin mr-2" /> Scoring opportunities…
        </div>
      ) : !data ? (
        <div className="ml-card p-12 text-center text-sm text-[var(--ml-text-secondary)]">
          Could not load opportunity data.
        </div>
      ) : (
        <>
          {/* AI narrative panel */}
          <div className="ml-card p-4 border-l-2 border-[var(--ml-primary)]" data-testid="ai-narrative-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <Robot size={22} weight="duotone" color="#0f62fe" />
                <div className="min-w-0">
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                    Claude Sonnet 4.5
                  </div>
                  <div className="font-semibold">AI consulting memo</div>
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-0.5">
                    {findingsCount} finding{findingsCount === 1 ? "" : "s"} across {totals.high_risk} risk · {totals.orphans} orphan · {totals.unjournaled} unjournaled · {totals.micro} microservice
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {narrative && (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="ml-btn-ghost flex items-center gap-1.5"
                    data-testid="copy-memo-btn"
                  >
                    {copied ? <CheckCircle size={13} weight="bold" /> : <CopySimple size={13} weight="bold" />}
                    {copied ? "Copied" : "Copy memo"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={narrative ? handleRegenerate : handleNarrate}
                  disabled={narrating || findingsCount === 0}
                  data-testid="generate-narrative-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-medium hover:bg-[var(--ml-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {narrating ? <Spinner size={13} className="animate-spin" /> : <Sparkle size={13} weight="bold" />}
                  {narrative ? "Regenerate" : "Generate AI recommendations"}
                </button>
              </div>
            </div>
            {cacheAge !== null && narrative && (
              <div
                className="mt-2 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] inline-flex items-center gap-1.5 px-1.5 py-0.5 bg-[var(--ml-surface)] border border-[var(--ml-border)]"
                data-testid="narrative-cache-badge"
                title="Click Regenerate to force a fresh Claude call (bypasses 60s cache)."
              >
                <span className="w-1.5 h-1.5 bg-[var(--ml-primary)] inline-block" />
                Cached · {cacheAge}s ago
              </div>
            )}
            {narrative ? (
              <div className="mt-4 border-t border-[var(--ml-border)] pt-4" data-testid="ai-narrative-body">
                {knowledge && (
                  <p className="text-xs text-[var(--ml-text-secondary)] mb-3" data-testid="ai-narrative-knowledge-line">
                    <span className="text-[var(--ml-text-primary)]">{knowledge.note}</span> {knowledge.grounded}
                    {knowledge.profileLine && <> <span data-testid="ai-narrative-knowledge-profile">{knowledge.profileLine}</span></>}
                  </p>
                )}
                <Markdown>{narrative}</Markdown>
              </div>
            ) : (
              <p className="text-xs text-[var(--ml-text-secondary)] mt-3">
                Click &ldquo;Generate AI recommendations&rdquo; — Claude will write a
                consulting-style memo using only the findings below. ~10–20s.
              </p>
            )}
          </div>

          {/* The 4 finding buckets */}
          <HighRiskWidget rows={data.high_risk_files} />
          <UnjournaledWidget rows={data.unjournaled_transactional} />
          <OrphansWidget rows={data.orphan_files} />
          <MicroserviceWidget rows={data.microservice_candidates} />
        </>
      )}
    </div>
  );
}

function FindingSection({ title, kicker, icon: Icon, color, count, testid, children }) {
  return (
    <section className="ml-card p-4" data-testid={testid}>
      <header className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <Icon size={20} weight="duotone" color={color} />
          <div className="min-w-0">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              {kicker}
            </div>
            <div className="font-semibold">{title}</div>
          </div>
        </div>
        <span
          className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5"
          style={{ background: `${color}1a`, color }}
          data-testid={`${testid}-count`}
        >
          {count} found
        </span>
      </header>
      {count === 0 ? (
        <p className="text-xs text-[var(--ml-text-secondary)]">No findings in this bucket.</p>
      ) : (
        children
      )}
    </section>
  );
}

/** Tiny right-aligned link button used inside finding-table rows.
 *  Deep-links into the Impact Graph page with the row's library+name pre-filled
 *  and a `from=opportunities` marker so that page can render a back-breadcrumb.
 */
function TraceImpactLink({ workspaceId, library, name, label = "Trace" }) {
  return (
    <Link
      to={`/workspaces/${workspaceId}/metadata/impact?object=${encodeURIComponent(name)}&library=${encodeURIComponent(library)}&from=opportunities`}
      data-testid={`trace-impact-${library}-${name}`}
      title={`Open dependency graph for ${library}/${name}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] hover:bg-[var(--ml-surface)] border border-transparent hover:border-[var(--ml-border)] transition-colors"
    >
      <Graph size={11} weight="bold" />
      {label}
    </Link>
  );
}

function HighRiskWidget({ rows }) {
  const { id: workspaceId } = useParams();
  return (
    <FindingSection
      testid="finding-high-risk"
      title="Highest blast-radius files"
      kicker="Critical risk"
      icon={WarningOctagon}
      color="#a2191f"
      count={rows.length}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Records</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgm refs</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Dependents</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Jrn</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Score</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.library_name}/${r.file_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
              <td className="px-2 py-1.5 font-mono text-xs">
                <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                {r.file_name}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {r.record_count != null ? r.record_count.toLocaleString() : "—"}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.program_ref_count}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.dependent_count}</td>
              <td className="px-2 py-1.5 text-right">
                {r.journaled ? (
                  <span className="ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">yes</span>
                ) : (
                  <span className="ml-mono text-[10px] uppercase tracking-widest text-[#a2191f]">no</span>
                )}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{r.risk_score}</td>
              <td className="px-2 py-1.5 text-right">
                <TraceImpactLink
                  workspaceId={workspaceId}
                  library={r.library_name}
                  name={r.file_name}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </FindingSection>
  );
}

function UnjournaledWidget({ rows }) {
  const { id: workspaceId } = useParams();
  return (
    <FindingSection
      testid="finding-unjournaled"
      title="Unjournaled transactional files (≥1k rows)"
      kicker="Recoverability risk"
      icon={ShieldWarning}
      color="#a56600"
      count={rows.length}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Records</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgm refs</th>
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Description</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.library_name}/${r.file_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
              <td className="px-2 py-1.5 font-mono text-xs">
                <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                {r.file_name}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.record_count != null ? r.record_count.toLocaleString() : "—"}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.program_ref_count}</td>
              <td className="px-2 py-1.5 text-xs text-[var(--ml-text-secondary)] truncate max-w-xs" title={r.text_description}>
                {r.text_description || "—"}
              </td>
              <td className="px-2 py-1.5 text-right">
                <TraceImpactLink
                  workspaceId={workspaceId}
                  library={r.library_name}
                  name={r.file_name}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </FindingSection>
  );
}

function OrphansWidget({ rows }) {
  const { id: workspaceId } = useParams();
  return (
    <FindingSection
      testid="finding-orphans"
      title="Orphan files with data (archive candidates)"
      kicker="Quick wins"
      icon={Archive}
      color="#525252"
      count={rows.length}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Records</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Size</th>
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Description</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.library_name}/${r.file_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
              <td className="px-2 py-1.5 font-mono text-xs">
                <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                {r.file_name}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.record_count != null ? r.record_count.toLocaleString() : "—"}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatBytes(r.file_size)}</td>
              <td className="px-2 py-1.5 text-xs text-[var(--ml-text-secondary)] truncate max-w-xs" title={r.text_description}>
                {r.text_description || "—"}
              </td>
              <td className="px-2 py-1.5 text-right">
                <TraceImpactLink
                  workspaceId={workspaceId}
                  library={r.library_name}
                  name={r.file_name}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </FindingSection>
  );
}

function MicroserviceWidget({ rows }) {
  const { id: workspaceId } = useParams();
  return (
    <FindingSection
      testid="finding-microservices"
      title="Microservice extraction candidates"
      kicker="Modernization roadmap"
      icon={Package}
      color="#0e6027"
      count={rows.length}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Program</th>
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Type</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Files</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgm calls</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Total refs</th>
            <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Description</th>
            <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.library_name}/${r.program_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
              <td className="px-2 py-1.5 font-mono text-xs">
                <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                {r.program_name}
              </td>
              <td className="px-2 py-1.5">
                <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                  {r.object_type || "—"}
                </span>
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.distinct_files}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.distinct_programs}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.total_refs}</td>
              <td className="px-2 py-1.5 text-xs text-[var(--ml-text-secondary)] truncate max-w-xs" title={r.text_description}>
                {r.text_description || "—"}
              </td>
              <td className="px-2 py-1.5 text-right">
                <TraceImpactLink
                  workspaceId={workspaceId}
                  library={r.library_name}
                  name={r.program_name}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </FindingSection>
  );
}

function formatBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
